import { quatToYaw } from "@uzverse/world-scene";
import type { SceneNode } from "@uzverse/world-scene";
import type { Collider } from "./camera/walk.js";
import { footprintOf } from "./generators.js";

/** A pickable root building or object seen from above: what collision and blueprint need. */
export interface Footprint {
  id: string;
  node: SceneNode;
  kind: "building" | "object";
  x: number;
  z: number;
  /** Yaw in radians (0 when the node has pitch or roll, which placements never do). */
  yaw: number;
  width: number;
  depth: number;
  height: number;
}

/** Footprints of root buildings and objects drawn by a box generator (trees and the like are skipped). */
export function footprintsOf(nodes: Iterable<SceneNode>): Footprint[] {
  const out: Footprint[] = [];
  for (const n of nodes) {
    if (n.parentId !== null || n.geometry.kind !== "procedural") continue;
    if (n.kind !== "building" && n.kind !== "object") continue;
    const { generator, params } = n.geometry;
    if (generator !== "building.box" && generator !== "prim.box") continue;
    const f = footprintOf(generator, params);
    out.push({
      id: n.id,
      node: n,
      kind: n.kind,
      x: n.transform.position[0],
      z: n.transform.position[2],
      yaw: quatToYaw(n.transform.rotation) ?? 0,
      width: f.width,
      depth: f.depth,
      height: f.height,
    });
  }
  return out;
}

/** Share of an asset's bounds that blocks walking (canopies and props are mostly air). */
const ASSET_SOLID_SHARE = 0.5;
/** Assets smaller than this (meters) never block the player. */
const ASSET_MIN_SIZE = 0.4;

/**
 * Walking colliders of asset-backed objects: their bounds, centred, scaled and turned like the
 * node. Kept apart from `footprintsOf` on purpose: footprints also feed the blueprint rules and
 * the street layout, where a tree or a bench must not count as a building.
 */
export function assetCollidersOf(nodes: Iterable<SceneNode>): Collider[] {
  const out: Collider[] = [];
  for (const n of nodes) {
    if (n.parentId !== null || n.kind !== "object" || n.geometry.kind !== "asset") continue;
    const { min, max } = n.geometry.bounds;
    const [sx = 1, sy = 1, sz = 1] = n.transform.scale ?? [1, 1, 1];
    const yaw = quatToYaw(n.transform.rotation) ?? 0;
    const width = (max[0] - min[0]) * sx;
    const depth = (max[2] - min[2]) * sz;
    if (Math.max(width, depth) < ASSET_MIN_SIZE) continue;
    // The bounds centre is not always the origin: rotate its offset by the node's yaw.
    const cx = ((min[0] + max[0]) / 2) * sx;
    const cz = ((min[2] + max[2]) / 2) * sz;
    out.push({
      x: n.transform.position[0] + cx * Math.cos(yaw) + cz * Math.sin(yaw),
      z: n.transform.position[2] - cx * Math.sin(yaw) + cz * Math.cos(yaw),
      yaw,
      hw: (width * ASSET_SOLID_SHARE) / 2,
      hd: (depth * ASSET_SOLID_SHARE) / 2,
      top: Math.max(0, max[1] * sy),
    });
  }
  return out;
}

/** Walking colliders: one oriented box per footprint (the building's whole body is solid). */
export function collidersOf(footprints: readonly Footprint[]): Collider[] {
  return footprints.map((f) => ({
    x: f.x,
    z: f.z,
    yaw: f.yaw,
    hw: f.width / 2,
    hd: f.depth / 2,
    top: f.height,
  }));
}
